import { generate } from "../util/guid";
import {
    ColorDefinition,
    FontData,
    FrameElement,
    FrameStepData,
    IllustrationElement,
    ImageElement,
    LayoutElementType,
    PictureStepData,
    Region,
    TextAlgorithm,
    TextboxElement,
    TextStepData,
} from "../types";
import { getFontMetrics, loadFont } from "../util/font";
import { generateFrameSVG, getFrameData } from "../util/frame";
import { applyTextTransformations, getTextAlignment, recomputeTextOnElement, textWidth } from "../text/shared";
import { getAttributesFromArrayBuffer } from "../util/image";
import { LayoutData } from "../CommandContext";
import { generateSVGWithUnknownColors } from "../util/illustration";
import { Pith } from "pith";
import { recalculateTextboxRegion } from "../text/algorithm/traditional";
import { domParser, fetch, fetchAsArrayBuffer, fetchAsString, xmlSerializer } from "../util/crossplatform";
import { createCanvas, getDomParser, loadImage } from "../customCanvas";

const nextIndexForLayer = (layout: LayoutData, layer: number): number => {
    const elements = layout.layoutState.elements.filter((el) => el.layer === layer);
    const maxIndex = Math.max(...(elements.map((el) => el.layerIndex).filter((li) => li !== undefined) as number[]));
    return Math.max(maxIndex, 0) + 1;
};

const getIllustrationBody = async (src: string): Promise<string> => {
    return new Promise((resolve) => {
        fetchAsString(src as string)
            .then((svg) => {
                resolve(svg);
            })
            .catch((e) => console.error(e));
    });
};

const svgPromiseCache = new Map<string, Promise<string>>();

/**
 * @param svg The SVG to get an object URL for.
 * @returns An object URL storing the cached image of this svg file.
 */
export const svgObjectURL = async (svg: string): Promise<string> => {
    if (svgPromiseCache.has(svg)) {
        return svgPromiseCache.get(svg)!;
    }

    const getCanvasObjectURLAsync = async (canvas: HTMLCanvasElement): Promise<string> => {
        return new Promise((resolve, reject) => {
            try {
                const canBuildObjectURL = !!URL && !!URL.createObjectURL;
                if (!canBuildObjectURL) throw new Error("Environment incapable of generating ObjectURL");
                canvas.toBlob((blob) => {
                    if (!blob) {
                        if (canvas.width === 0 || canvas.height === 0)
                            throw new Error(`Canvas dimensions are invalid (${canvas.width},${canvas.height})`);
                        else if (canvas.width * canvas.height >= 268435456)
                            throw new Error(`Canvas dimensions exceed device limit (${canvas.width},${canvas.height})`);
                        throw new Error("Couldn't generate object URL for Illustration, the blob was undefined!");
                    }
                    resolve(URL.createObjectURL(blob));
                });
            } catch (e: any) {
                reject(e);
            }
        });
    };

    const getPromise = async () => {
        const parser = domParser();
        const parsedSvg = parser.parseFromString(svg, "image/svg+xml");
        const root = parsedSvg.firstElementChild;

        if (!root) {
            throw new Error("Failed to read SVG");
        }

        const serialiser = xmlSerializer();
        const rebuiltSvg = serialiser.serializeToString(root);
        const canvas = createCanvas();
        const ctx = canvas.getContext("2d")!;
        const pith = await Pith.from(ctx as any, rebuiltSvg, {
            anonymousCrossOrigin: true,
            ignoreDimensions: false,
            createCanvas: createCanvas as any,
            createImage: loadImage as any,
            DOMParser: getDomParser(),
            fetch: fetch,
        });

        const widthString = root.getAttribute("width");
        const heightString = root.getAttribute("height");
        const cachedIllustrationQuality = 2048;
        if (heightString && widthString) {
            const height = parseFloat(heightString);
            const width = parseFloat(widthString);
            const aspect = width / height;
            if (aspect > 1) {
                // Wider than high
                pith.resize(cachedIllustrationQuality, cachedIllustrationQuality / aspect);
            } else {
                // Higher than wide
                pith.resize(cachedIllustrationQuality * aspect, cachedIllustrationQuality);
            }
        } else {
            pith.resize(cachedIllustrationQuality, cachedIllustrationQuality);
        }

        await pith.render();
        return await getCanvasObjectURLAsync(canvas as any);
    };
    const promise = getPromise();
    svgPromiseCache.set(svg, promise);
    return promise;
};

/**
 * Calculate a centered and reasonably sized region for the layout and content.
 */
const centralRegion = async (
    layout: LayoutData,
    type: LayoutElementType,
    link?: string,
    opts?: { text?: string; fontScale?: number },
): Promise<Region> => {
    const coords = (layout.layoutState.layout.useEditableArea && layout.layoutState.layout.editableArea) || {
        width: layout.layoutState.layout.width,
        height: layout.layoutState.layout.height,
        x: 0,
        y: 0,
    };
    const lesserDim = coords.width < coords.height ? coords.width : coords.height;
    const size = layout.layoutState.layout.useEditableArea ? lesserDim / 1.3 : lesserDim / 2;

    const region = {
        top: coords.y + coords.height / 2 - size / 2,
        left: coords.x + coords.width / 2 - size / 2,
        width: size,
        height: size,
        rotation: 0,
        panelId: layout.layoutState.layout.panelId,
    };

    if (link && type === LayoutElementType.Illustration) {
        const svgData = await generateSVGWithUnknownColors(await getIllustrationBody(link));
        const parser = domParser();
        const parsedSvg = parser.parseFromString(svgData.svg, "image/svg+xml");
        const svgEl = parsedSvg.firstElementChild as SVGElement;
        const viewBox = svgEl.getAttribute("viewBox");
        if (!viewBox) throw new Error("SVG missing viewBox.");
        const oldHeight = region.height;
        const viewBoxElems = viewBox.split(" ");
        const viewBoxWidth = parseFloat(viewBoxElems[2]) || 1;
        const viewBoxHeight = parseFloat(viewBoxElems[3]) || 1;
        const aspect = viewBoxWidth / viewBoxHeight;
        region.height = region.width / aspect;
        region.top += (oldHeight - region.height) / 2;
    }

    if (link && type === LayoutElementType.Image) {
        const oldHeight = region.height;
        const arrayBuffer = await fetchAsArrayBuffer(link);
        const attributes = await getAttributesFromArrayBuffer(arrayBuffer);
        const aspect = attributes.width / attributes.height;
        region.height = region.width / aspect;
        region.top += (oldHeight - region.height) / 2;
    }

    // When font size & text are provided, we compute based on the calculated text width
    if (link && type === LayoutElementType.Textbox && opts?.text && opts?.fontScale) {
        await loadFont(link);
        const cache = getFontMetrics(link);
        const calculatedTextWidth = textWidth(opts.text, opts?.fontScale, cache);
        region.width = Math.min(calculatedTextWidth, coords.width * 0.85);
        region.left = coords.x + coords.width / 2 - region.width / 2;
    }

    return region;
};

export class LayoutElementFactory {
    static async getFrame(
        layout: LayoutData,
        opts: {
            region?: Region;
            configuration: Partial<FrameStepData>;
            src?: string;
        },
    ): Promise<FrameElement> {
        const frameSvg = await generateFrameSVG(opts.region, opts.src);
        const frameData = await getFrameData(frameSvg);

        const region = opts.region || (await centralRegion(layout, LayoutElementType.Frame));

        return {
            id: generate(),
            x: region.left,
            y: region.top,
            width: region.width,
            height: region.height,
            layer: region.layer || 0,
            layerIndex: region.layerIndex || nextIndexForLayer(layout, region.layer || 0),
            rotation: region.rotation,
            scaleX: region.width / frameData.width,
            scaleY: region.height / frameData.height,
            path: frameData.path,
            dataWidth: frameData.width,
            dataHeight: frameData.height,
            type: LayoutElementType.Frame,
            disablePlaceholder: opts.configuration.disablePlaceholder,
            focalBlur: opts.configuration.focalBlur,
            focalBlurStrength: opts.configuration.focalBlurStrength,
            focalBlurRadius: opts.configuration.focalBlurRadius,
            forceImageCover: opts.configuration.forceImageCover,
            pattern: undefined,
            immutable: region.immutable,
        };
    }

    static async getImage(
        layout: LayoutData,
        opts: {
            region?: Region;
            layout?: LayoutData;
            configuration: Partial<PictureStepData>;
            src: string;
        },
    ): Promise<ImageElement> {
        const region = opts.region || (await centralRegion(layout, LayoutElementType.Image, opts.src));

        return {
            id: generate(),
            src: opts.src,
            type: LayoutElementType.Image,
            y: region.top,
            x: region.left,
            rotation: region.rotation,
            width: region.width,
            height: region.height,
            layer: region.layer || 0,
            layerIndex: region.layerIndex || nextIndexForLayer(layout, region.layer || 0),
            immutable: region.immutable,
            preserveAspectRatio: "none",
        };
    }

    static async getTextbox(
        layout: LayoutData,
        opts: {
            region?: Region;
            configuration: Partial<TextStepData>;
            fontSrc: string;
            designInputStep?: any; // TextDesignInputStepData old generation type from headless
        },
    ): Promise<TextboxElement> {
        const { configuration, fontSrc, designInputStep } = opts;

        // Load the font requested.
        const font = await loadFont(fontSrc);
        const fontData: FontData = {
            assetUrl: fontSrc,
            name: font.names.fullName["en"],
        };

        const inputText = designInputStep?.text || configuration.defaultText || "";
        const replacedText = configuration.replaceableText
            ? configuration.replaceableText.replace("{{}}", inputText)
            : inputText;

        // Transform based on configuration
        const text = applyTextTransformations(replacedText, {
            vertical: configuration.vertical,
            uppercase: configuration.uppercase,
        });

        const region =
            opts.region ||
            (await centralRegion(layout, LayoutElementType.Textbox, fontSrc, {
                text,
                fontScale: configuration.size ? configuration.size / font.unitsPerEm : undefined,
            }));

        const recomputedElement = recomputeTextOnElement(
            {
                id: generate(),
                type: LayoutElementType.Textbox,
                x: region.left,
                y: region.top,
                width: region.width,
                height: region.height,
                align: getTextAlignment(configuration.textAlign, configuration.vertical),
                curved: configuration.curved,
                fill: designInputStep?.color || configuration.colour || "#000000",
                fontData,
                layer: region.layer || 0,
                layerIndex: region.layerIndex || nextIndexForLayer(layout, region.layer || 0),
                paths: configuration.paths,
                rotation: region.rotation,
                vertical: configuration.vertical,
                verticalAlign: configuration.verticalAlign || "middle",
                algorithm: TextAlgorithm.Traditional,
                fontSize: configuration.size || Math.max(Math.round(region.height * 0.025), 1),
                text,
                input: inputText,
            },
            text,
        );
        const fittedRegion = recalculateTextboxRegion(
            { ...region, height: recomputedElement.height },
            fontData,
            recomputedElement.fontSize,
            recomputedElement?.text?.split("\n") || [],
            recomputedElement.align,
        );
        return {
            ...recomputedElement,
            x: fittedRegion.left,
            y: fittedRegion.top + (region.height - fittedRegion.height) / 2,
            width: fittedRegion.width,
            height: fittedRegion.height,
        };
    }

    static async getShape(
        layout: LayoutData,
        opts: { region?: { item: Region; index: number }; color: string },
    ): Promise<IllustrationElement> {
        const svg = `
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        xmlSpace="preserve"
                        preserveAspectRatio="none"
                        version="1.1"
                        width="1"
                        height="1"
                        viewBox="0 0 1 1"
                    >
                        <rect
                            x="0"
                            y="0"
                            width="1"
                            height="1"
                            class="spiff-fill-shape"
                            fill="${opts.color}"
                        />
                    </svg>
                `;
        const colors: { [key: string]: ColorDefinition } = {};
        colors["spiff-fill-shape"] = { browserValue: opts.color };

        const region = opts.region?.item || (await centralRegion(layout, LayoutElementType.Illustration));

        const elementKey = generate();
        return {
            stepRegion: opts.region?.item,
            stepRegionIndex: opts.region?.index,
            colors,
            id: elementKey,
            svg,
            type: LayoutElementType.Illustration,
            y: region.top,
            x: region.left,
            rotation: region.rotation,
            width: region.width,
            height: region.height,
            layer: region.layer || 0,
            layerIndex: region.layerIndex || nextIndexForLayer(layout, region.layer || 0),
            immutable: region.immutable,
        };
    }

    static async getIllustration(
        layout: LayoutData,
        opts: { region?: { item: Region; index: number }; src: string },
    ): Promise<IllustrationElement> {
        const region = opts.region?.item || (await centralRegion(layout, LayoutElementType.Illustration, opts.src));

        const svgData = await generateSVGWithUnknownColors(await getIllustrationBody(opts.src));
        const objectURL = await svgObjectURL(svgData.svg);
        const elementKey = generate();
        return {
            cachedObjectURL: objectURL,
            stepRegion: opts.region?.item,
            stepRegionIndex: opts.region?.index,
            colors: svgData.colors,
            id: elementKey,
            src: opts.src,
            svg: svgData.svg,
            type: LayoutElementType.Illustration,
            y: region.top,
            x: region.left,
            rotation: region.rotation,
            width: region.width,
            height: region.height,
            layer: region.layer || 0,
            layerIndex: region.layerIndex || nextIndexForLayer(layout, region.layer || 0),
            immutable: region.immutable,
        };
    }
}
